import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Users, Crown, FileText, Inbox, Plus, Trash2, Check, BarChart3,
  Sparkles, Wand2, Bell, Target, Loader2, ClipboardList, Ticket, Copy, Search, ScrollText, UserX, MapPin, FileAudio,
} from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview", icon: BarChart3 },
  { key: "users", label: "Users", icon: Users },
  { key: "promo", label: "Promo codes", icon: Ticket },
  { key: "topics", label: "Topics", icon: FileText },
  { key: "drafts", label: "AI Drafts", icon: Wand2 },
  { key: "submissions", label: "Submissions", icon: Inbox },
  { key: "calibration", label: "Calibration", icon: Target },
];

const CEFR_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [topics, setTopics] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [form, setForm] = useState({ type: "writing", task: "", content: "" });
  const [running, setRunning] = useState("");

  // calibration state
  const [gold, setGold] = useState([]);
  const [tasks, setTasks] = useState({ writing: [], speaking: [] });
  const [gForm, setGForm] = useState({ skill: "writing", task_id: "", level_cefr: "B2", score_20: "", text: "" });
  const [savingGold, setSavingGold] = useState(false);
  const [evalMap, setEvalMap] = useState({});
  const [evaluating, setEvaluating] = useState("");

  // users + promo state
  const [users, setUsers] = useState([]);
  const [busyUser, setBusyUser] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [promos, setPromos] = useState([]);
  const [pForm, setPForm] = useState({ code: "", months: 3, max_uses: "", note: "" });
  const [savingPromo, setSavingPromo] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => console.error("stats", e));
    api.get("/admin/topics").then((r) => setTopics(r.data)).catch((e) => console.error("topics", e));
    api.get("/admin/drafts").then((r) => setDrafts(r.data)).catch((e) => console.error("drafts", e));
    api.get("/admin/submissions").then((r) => setSubmissions(r.data)).catch((e) => console.error("subs", e));
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadGold = useCallback(() => {
    api.get("/admin/gold-samples").then((r) => setGold(r.data)).catch((e) => console.error("gold", e));
    api.get("/tcf/writing-tasks").then((r) => setTasks((t) => ({ ...t, writing: r.data }))).catch(() => {});
    api.get("/tcf/speaking-tasks").then((r) => setTasks((t) => ({ ...t, speaking: r.data }))).catch(() => {});
  }, []);
  useEffect(() => { if (tab === "calibration") loadGold(); }, [tab, loadGold]);

  const loadUsers = useCallback(() => { api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => console.error("users", e)); }, []);
  const loadPromos = useCallback(() => { api.get("/admin/promo-codes").then((r) => setPromos(r.data)).catch((e) => console.error("promos", e)); }, []);
  useEffect(() => { if (tab === "users") loadUsers(); if (tab === "promo") loadPromos(); }, [tab, loadUsers, loadPromos]);

  const setPremium = async (uid, months) => {
    setBusyUser(uid);
    try { await api.post(`/admin/users/${uid}/premium`, { months }); toast.success(months > 0 ? `Premium granted (${months} mo).` : "Premium revoked."); loadUsers(); }
    catch { toast.error("Could not update."); }
    finally { setBusyUser(""); }
  };
  const deleteUser = async (uid, name) => {
    if (!window.confirm(`Delete ${name || "this user"}? This permanently removes their account and data. This cannot be undone.`)) return;
    setBusyUser(uid);
    try { await api.delete(`/admin/users/${uid}`); toast.success("User deleted."); loadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete user."); }
    finally { setBusyUser(""); }
  };
  const [purging, setPurging] = useState(false);
  const cleanupTestData = async () => {
    setPurging(true);
    try {
      const { data: preview } = await api.post("/admin/cleanup-test-data?dry_run=true");
      const emails = preview.emails || [];
      const msg = [
        "This will PERMANENTLY remove from THIS environment:",
        "",
        `• ${preview.users_deleted} test/demo account(s):`,
        emails.length ? emails.map((e) => `   - ${e}`).join("\n") : "   (none)",
        "",
        `• ${preview.topics_deleted} test topic(s)`,
        "",
        "Admin accounts are always protected. This cannot be undone. Continue?",
      ].join("\n");
      if (!window.confirm(msg)) { setPurging(false); return; }
      const { data } = await api.post("/admin/cleanup-test-data");
      toast.success(`Cleaned up: ${data.users_deleted} account(s), ${data.topics_deleted} topic(s).`);
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Cleanup failed."); }
    finally { setPurging(false); }
  };
  const createPromo = async () => {
    if (!pForm.code.trim() || !pForm.months) { toast.error("Enter a code and months."); return; }
    setSavingPromo(true);
    try {
      const payload = { code: pForm.code, months: Number(pForm.months), note: pForm.note, max_uses: pForm.max_uses === "" ? null : Number(pForm.max_uses) };
      const { data } = await api.post("/admin/promo-codes", payload);
      setPromos((p) => [data, ...p]); setPForm({ code: "", months: 3, max_uses: "", note: "" });
      toast.success("Promo code created.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create code."); }
    finally { setSavingPromo(false); }
  };
  const deletePromo = async (id) => { await api.delete(`/admin/promo-codes/${id}`); setPromos((p) => p.filter((x) => x.id !== id)); };
  const copyCode = (code) => { navigator.clipboard?.writeText(code); toast.success(`Copied "${code}"`); };

  const filteredUsers = users.filter((u) => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return true;
    return `${u.name} ${u.email} ${u.target_level} ${u.referred_by}`.toLowerCase().includes(q);
  });

  const addTopic = async () => {
    if (!form.content.trim()) return;
    try {
      const { data } = await api.post("/admin/topics", form);
      setTopics((t) => [data, ...t]); setForm({ type: form.type, task: "", content: "" });
      toast.success("Topic published.");
    } catch { toast.error("Could not add topic."); }
  };
  const deleteTopic = async (id) => { await api.delete(`/admin/topics/${id}`); setTopics((t) => t.filter((x) => x.id !== id)); setDrafts((d) => d.filter((x) => x.id !== id)); };
  const approveDraft = async (id) => {
    try { await api.post(`/admin/topics/${id}/approve`); toast.success("Approved & published."); setDrafts((d) => d.filter((x) => x.id !== id)); load(); }
    catch { toast.error("Could not approve."); }
  };
  const publishSub = async (id) => {
    try { await api.post(`/admin/submissions/${id}/publish`); toast.success("Published to dashboard."); load(); }
    catch { toast.error("Could not publish."); }
  };

  const runJob = async (kind) => {
    setRunning(kind);
    try {
      const { data } = await api.post(`/admin/run-${kind}`);
      if (kind === "curation") toast.success(`Curation done — ${data.drafts} draft(s) from ${data.processed} submission(s).`);
      else toast.success(`Follow-ups sent to ${data.notified} student(s).`);
      load();
    } catch { toast.error("Job failed. Check server logs."); }
    finally { setRunning(""); }
  };

  const addGold = async () => {
    if (gForm.text.trim().length < 10) { toast.error("Paste the graded response text."); return; }
    setSavingGold(true);
    try {
      const payload = { ...gForm, score_20: gForm.score_20 === "" ? null : Number(gForm.score_20) };
      const { data } = await api.post("/admin/gold-samples", payload);
      setGold((g) => [data, ...g]);
      setGForm({ skill: gForm.skill, task_id: "", level_cefr: "B2", score_20: "", text: "" });
      toast.success("Gold sample saved. The scorer will now anchor to it.");
    } catch { toast.error("Could not save sample."); }
    finally { setSavingGold(false); }
  };
  const deleteGold = async (id) => { await api.delete(`/admin/gold-samples/${id}`); setGold((g) => g.filter((x) => x.id !== id)); setEvalMap((m) => { const c = { ...m }; delete c[id]; return c; }); };
  const evalGold = async (id) => {
    setEvaluating(id);
    try { const { data } = await api.post(`/admin/gold-samples/${id}/evaluate`); setEvalMap((m) => ({ ...m, [id]: data })); }
    catch { toast.error("Evaluation failed."); }
    finally { setEvaluating(""); }
  };

  const statCards = stats ? [
    { label: "Students", value: stats.students, icon: Users },
    { label: "Premium", value: stats.premium, icon: Crown },
    { label: "Topics", value: stats.topics, icon: FileText },
    { label: "Pending submissions", value: stats.pending_submissions, icon: Inbox },
  ] : [];

  return (
    <div data-testid="admin-page">
      <h1 className="font-display text-4xl font-semibold">Admin</h1>
      <p className="text-muted-foreground mt-2">Manage topics, approve AI-curated drafts, review submissions, and calibrate the scorer.</p>
      <a href="/admin/transcripts" data-testid="admin-transcripts-link" className="mt-3 inline-flex items-center gap-2 px-4 h-10 rounded-full bg-amber-100 text-amber-900 border border-amber-300 text-sm font-semibold hover:bg-amber-200 transition-colors">
        <ScrollText className="h-4 w-4" /> Listening transcripts editor
      </a>
      <a href="/admin/media" data-testid="admin-media-link" className="mt-3 sm:ml-3 inline-flex items-center gap-2 px-4 h-10 rounded-full bg-emerald-100 text-emerald-900 border border-emerald-300 text-sm font-semibold hover:bg-emerald-200 transition-colors">
        <FileAudio className="h-4 w-4" /> Exam media manager
      </a>
      <button onClick={cleanupTestData} disabled={purging} data-testid="cleanup-test-data-btn" className="mt-3 sm:ml-3 inline-flex items-center gap-2 px-4 h-10 rounded-full bg-secondary text-foreground border border-border text-sm font-semibold hover:bg-destructive hover:text-destructive-foreground transition-colors disabled:opacity-60">
        {purging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Purge test/demo data
      </button>

      <div className="mt-6 flex flex-wrap gap-2">
        {TABS.map((t) => {
          const Icon = t.icon;
          const count = t.key === "drafts" ? drafts.length : 0;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.key ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-accent"}`}>
              <Icon className="h-4 w-4" /> {t.label}
              {count > 0 && <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-accent text-accent-foreground">{count}</span>}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <div data-testid="admin-overview">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            {statCards.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} className="rounded-2xl bg-card border border-border p-5">
                  <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">{s.label}</span><Icon className="h-4 w-4 text-primary" /></div>
                  <div className="mt-2 text-3xl font-display font-semibold">{s.value}</div>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-6 rounded-2xl bg-card border border-border p-6">
            <h2 className="font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Automation</h2>
            <p className="text-sm text-muted-foreground mt-1">These jobs run daily on a schedule. Trigger them now if needed.</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button onClick={() => runJob("curation")} disabled={!!running} data-testid="run-curation-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50">
                {running === "curation" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Curate submissions → drafts
              </button>
              <button onClick={() => runJob("followups")} disabled={!!running} data-testid="run-followups-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-secondary hover:bg-accent text-sm font-semibold disabled:opacity-50">
                {running === "followups" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />} Send post-exam follow-ups
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === "topics" && (
        <div className="mt-6 grid lg:grid-cols-3 gap-5" data-testid="admin-topics">
          <div className="rounded-2xl bg-card border border-border p-6">
            <h2 className="font-semibold mb-3">Add a topic</h2>
            <div className="flex gap-2 mb-3">
              {["writing", "speaking"].map((t) => (
                <button key={t} onClick={() => setForm((f) => ({ ...f, type: t }))} data-testid={`admin-topic-type-${t}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ${form.type === t ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{t === "writing" ? "Writing" : "Speaking"}</button>
              ))}
            </div>
            <input value={form.task} onChange={(e) => setForm((f) => ({ ...f, task: e.target.value }))} placeholder="Task label (e.g. Tâche 3)" data-testid="admin-topic-task"
              className="w-full h-10 px-3 mb-2 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            <textarea value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} rows={4} placeholder="Topic text (in French)" data-testid="admin-topic-content"
              className="w-full p-3 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none" />
            <button onClick={addTopic} disabled={!form.content.trim()} data-testid="admin-add-topic-btn"
              className="mt-2 w-full h-10 rounded-xl bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"><Plus className="h-4 w-4" /> Publish topic</button>
          </div>

          <div className="lg:col-span-2 space-y-2">
            {topics.length === 0 && <p className="text-sm text-muted-foreground">No topics yet.</p>}
            {topics.map((t) => (
              <div key={t.id} className="rounded-xl bg-card border border-border p-4 flex items-start justify-between gap-3" data-testid={`admin-topic-${t.id}`}>
                <div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className={`font-bold ${t.type === "writing" ? "text-sky-600" : "text-primary"}`}>{t.type === "writing" ? "✍️ Writing" : "🎙️ Speaking"}</span>
                    {t.task && <span className="text-muted-foreground">{t.task}</span>}
                    <span className="text-muted-foreground">· {t.source}</span>
                  </div>
                  <p className="text-sm mt-1">{t.content}</p>
                </div>
                <button onClick={() => deleteTopic(t.id)} data-testid={`delete-topic-${t.id}`} className="text-destructive shrink-0"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "drafts" && (
        <div className="mt-6 space-y-2" data-testid="admin-drafts">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground flex items-center gap-2"><ClipboardList className="h-4 w-4" /> AI-curated drafts from student submissions. Approve to publish on the dashboard feed.</p>
            <button onClick={() => runJob("curation")} disabled={!!running} data-testid="drafts-run-curation-btn"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold disabled:opacity-50">
              {running === "curation" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Run curation now
            </button>
          </div>
          {drafts.length === 0 && <p className="text-sm text-muted-foreground">No pending drafts. Run curation to generate them from student submissions.</p>}
          {drafts.map((d) => (
            <div key={d.id} className="rounded-xl bg-card border border-border p-4 flex items-start justify-between gap-3" data-testid={`draft-${d.id}`}>
              <div>
                <div className="flex items-center gap-2 text-xs">
                  <span className={`font-bold ${d.type === "writing" ? "text-sky-600" : "text-primary"}`}>{d.type === "writing" ? "✍️ Writing" : "🎙️ Speaking"}</span>
                  {d.task && <span className="text-muted-foreground">{d.task}</span>}
                  <span className="px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">draft · AI</span>
                </div>
                <p className="text-sm mt-1">{d.content}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => approveDraft(d.id)} data-testid={`approve-draft-${d.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold"><Check className="h-3.5 w-3.5" /> Approve</button>
                <button onClick={() => deleteTopic(d.id)} data-testid={`reject-draft-${d.id}`} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "submissions" && (
        <div className="mt-6 space-y-2" data-testid="admin-submissions">
          {submissions.length === 0 && <p className="text-sm text-muted-foreground">No submissions yet.</p>}
          {submissions.map((s) => (
            <div key={s.id} className="rounded-xl bg-card border border-border p-4 flex items-start justify-between gap-3" data-testid={`submission-${s.id}`}>
              <div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-bold text-primary">{s.type === "writing" ? "✍️ Writing" : "🎙️ Speaking"}</span>
                  <span className={`px-2 py-0.5 rounded-full ${s.status === "published" ? "bg-accent text-accent-foreground" : "bg-secondary"}`}>{s.status}</span>
                </div>
                <p className="text-sm mt-1">{s.content}</p>
                {s.exam_location && <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.exam_location}</p>}
              </div>
              {s.status === "pending" && (
                <button onClick={() => publishSub(s.id)} data-testid={`publish-sub-${s.id}`} className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold"><Check className="h-3.5 w-3.5" /> Publish</button>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "calibration" && (
        <div className="mt-6 grid lg:grid-cols-3 gap-5" data-testid="admin-calibration">
          <div className="rounded-2xl bg-card border border-border p-6">
            <h2 className="font-semibold flex items-center gap-2"><Target className="h-4 w-4 text-primary" /> Add a gold sample</h2>
            <p className="text-xs text-muted-foreground mt-1 mb-3">Paste a response that an official examiner graded. The AI scorer uses these as anchors to match the real TCF standard.</p>
            <div className="flex gap-2 mb-3">
              {["writing", "speaking"].map((t) => (
                <button key={t} onClick={() => setGForm((f) => ({ ...f, skill: t, task_id: "" }))} data-testid={`gold-skill-${t}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ${gForm.skill === t ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{t === "writing" ? "Writing" : "Speaking"}</button>
              ))}
            </div>
            <label className="text-xs font-semibold text-muted-foreground">Task (optional)</label>
            <select value={gForm.task_id} onChange={(e) => setGForm((f) => ({ ...f, task_id: e.target.value }))} data-testid="gold-task-select"
              className="w-full h-10 px-3 mt-1 mb-3 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring">
              <option value="">General (no specific task)</option>
              {(tasks[gForm.skill] || []).map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Official CEFR</label>
                <select value={gForm.level_cefr} onChange={(e) => setGForm((f) => ({ ...f, level_cefr: e.target.value }))} data-testid="gold-cefr-select"
                  className="w-full h-10 px-3 mt-1 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                  {CEFR_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Score /20 (optional)</label>
                <input type="number" min="0" max="20" value={gForm.score_20} onChange={(e) => setGForm((f) => ({ ...f, score_20: e.target.value }))} placeholder="e.g. 12" data-testid="gold-score-input"
                  className="w-full h-10 px-3 mt-1 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
              </div>
            </div>
            <label className="text-xs font-semibold text-muted-foreground">Graded response text</label>
            <textarea value={gForm.text} onChange={(e) => setGForm((f) => ({ ...f, text: e.target.value }))} rows={6} placeholder="Paste the student's exact response that was officially graded…" data-testid="gold-text-input"
              className="w-full p-3 mt-1 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none" />
            <button onClick={addGold} disabled={savingGold || gForm.text.trim().length < 10} data-testid="gold-save-btn"
              className="mt-3 w-full h-10 rounded-xl bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {savingGold ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save gold sample
            </button>
          </div>

          <div className="lg:col-span-2 space-y-2">
            {gold.length === 0 && <p className="text-sm text-muted-foreground">No gold samples yet. Add officially-graded responses to anchor the scorer.</p>}
            {gold.map((s) => {
              const ev = evalMap[s.id];
              return (
                <div key={s.id} className="rounded-xl bg-card border border-border p-4" data-testid={`gold-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-xs flex-wrap">
                        <span className={`font-bold ${s.skill === "writing" ? "text-sky-600" : "text-primary"}`}>{s.skill === "writing" ? "✍️ Writing" : "🎙️ Speaking"}</span>
                        <span className="px-2 py-0.5 rounded-full bg-accent text-accent-foreground font-semibold">{s.level_cefr}{s.score_20 != null ? ` · ${s.score_20}/20` : ""}</span>
                      </div>
                      <p className="text-sm mt-1.5 line-clamp-2 text-muted-foreground">{s.text}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => evalGold(s.id)} disabled={evaluating === s.id} data-testid={`gold-eval-${s.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary hover:bg-accent text-xs font-semibold disabled:opacity-50">
                        {evaluating === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Target className="h-3.5 w-3.5" />} Check drift
                      </button>
                      <button onClick={() => deleteGold(s.id)} data-testid={`gold-delete-${s.id}`} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  {ev && (
                    <div className="mt-3 pt-3 border-t border-border grid grid-cols-3 gap-3 text-center" data-testid={`gold-drift-${s.id}`}>
                      <div><p className="text-[11px] text-muted-foreground">AI predicted</p><p className="font-display font-semibold">CLB {ev.predicted.clb} · {ev.predicted.cefr}</p></div>
                      <div><p className="text-[11px] text-muted-foreground">Official</p><p className="font-display font-semibold">{ev.actual.clb != null ? `CLB ${ev.actual.clb}` : "—"} · {ev.actual.level_cefr}</p></div>
                      <div><p className="text-[11px] text-muted-foreground">Drift</p><p className={`font-display font-semibold ${ev.drift == null ? "" : Math.abs(ev.drift) <= 1 ? "text-primary" : "text-destructive"}`}>{ev.drift == null ? "n/a" : (ev.drift > 0 ? `+${ev.drift}` : ev.drift)}</p></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "users" && (
        <div className="mt-6" data-testid="admin-users">
          <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input value={userQuery} onChange={(e) => setUserQuery(e.target.value)} placeholder="Search by name or email…" data-testid="user-search"
                className="w-full h-10 pl-9 pr-3 rounded-full bg-card border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
            <span className="text-xs text-muted-foreground">{filteredUsers.length} of {users.length} users</span>
          </div>
          <div className="rounded-2xl bg-card border border-border overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-muted-foreground border-b border-border">
              <div className="col-span-4">User</div><div className="col-span-3">Level / Ref</div><div className="col-span-2">Access</div><div className="col-span-3 text-right">Grant premium</div>
            </div>
            {filteredUsers.length === 0 && <p className="p-4 text-sm text-muted-foreground">No users match your search.</p>}
            {filteredUsers.map((u) => (
              <div key={u.id} className="grid grid-cols-12 gap-2 px-4 py-3 items-center border-b border-border last:border-0" data-testid={`user-row-${u.id}`}>
                <div className="col-span-4 min-w-0">
                  <p className="font-semibold text-sm truncate">{u.name || "—"} {u.role === "admin" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent text-accent-foreground">admin</span>}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                </div>
                <div className="col-span-3 text-sm"><span className="font-semibold">{u.target_level || "—"}</span>{u.referred_by && <span className="block text-xs text-muted-foreground truncate">via {u.referred_by}</span>}</div>
                <div className="col-span-2">
                  {u.is_premium ? <span className="inline-flex items-center gap-1 text-xs font-bold text-primary" data-testid={`user-premium-${u.id}`}><Crown className="h-3.5 w-3.5" /> Premium</span>
                   : u.trial_active ? <span className="text-xs font-semibold text-muted-foreground">Trial</span>
                   : <span className="text-xs text-muted-foreground">Free</span>}
                  {u.premium_until && <span className="block text-[10px] text-muted-foreground">till {new Date(u.premium_until).toLocaleDateString()}</span>}
                </div>
                <div className="col-span-3 flex items-center justify-end gap-1.5">
                  {busyUser === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <>
                    {[1, 3, 6, 12].map((m) => (
                      <button key={m} onClick={() => setPremium(u.id, m)} data-testid={`grant-${m}mo-${u.id}`} title={`Grant ${m} month(s)`}
                        className="h-7 w-7 rounded-lg bg-secondary hover:bg-primary hover:text-primary-foreground text-xs font-bold transition-colors">{m}</button>
                    ))}
                    {u.is_premium && <button onClick={() => setPremium(u.id, 0)} data-testid={`revoke-${u.id}`} title="Revoke premium" className="h-7 w-7 rounded-lg bg-secondary hover:bg-destructive hover:text-destructive-foreground grid place-items-center"><Trash2 className="h-3.5 w-3.5" /></button>}
                    {u.role !== "admin" && <button onClick={() => deleteUser(u.id, u.name)} data-testid={`delete-user-${u.id}`} title="Delete user" className="h-7 w-7 rounded-lg bg-secondary hover:bg-destructive hover:text-destructive-foreground grid place-items-center"><UserX className="h-3.5 w-3.5" /></button>}
                  </>}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Tip: the small buttons grant 1 / 3 / 6 / 12 months of Premium. Grants stack on any existing time.</p>
        </div>
      )}

      {tab === "promo" && (
        <div className="mt-6 grid lg:grid-cols-3 gap-5" data-testid="admin-promo">
          <div className="rounded-2xl bg-card border border-border p-6">
            <h2 className="font-semibold flex items-center gap-2"><Ticket className="h-4 w-4 text-primary" /> Create a promo code</h2>
            <p className="text-xs text-muted-foreground mt-1 mb-3">Give partners/institutions a code. Students redeem it on the Premium page to unlock the months you set.</p>
            <label className="text-xs font-semibold text-muted-foreground">Code</label>
            <input value={pForm.code} onChange={(e) => setPForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="ELS-PARTNER-2026" data-testid="promo-code-input"
              className="w-full h-10 px-3 mt-1 mb-3 rounded-xl bg-background border border-border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring" />
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Months</label>
                <input type="number" min="1" value={pForm.months} onChange={(e) => setPForm((f) => ({ ...f, months: e.target.value }))} data-testid="promo-months-input"
                  className="w-full h-10 px-3 mt-1 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Max uses</label>
                <input type="number" min="1" value={pForm.max_uses} onChange={(e) => setPForm((f) => ({ ...f, max_uses: e.target.value }))} placeholder="∞" data-testid="promo-maxuses-input"
                  className="w-full h-10 px-3 mt-1 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
              </div>
            </div>
            <label className="text-xs font-semibold text-muted-foreground">Note (optional)</label>
            <input value={pForm.note} onChange={(e) => setPForm((f) => ({ ...f, note: e.target.value }))} placeholder="e.g. ELS Institute, Delhi" data-testid="promo-note-input"
              className="w-full h-10 px-3 mt-1 mb-3 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            <button onClick={createPromo} disabled={savingPromo} data-testid="promo-create-btn"
              className="w-full h-10 rounded-xl bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {savingPromo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create code
            </button>
          </div>

          <div className="lg:col-span-2 space-y-2">
            {promos.length === 0 && <p className="text-sm text-muted-foreground">No promo codes yet. Create one to share with a partner.</p>}
            {promos.map((p) => (
              <div key={p.id} className="rounded-xl bg-card border border-border p-4 flex items-center justify-between gap-3" data-testid={`promo-${p.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold">{p.code}</span>
                    <button onClick={() => copyCode(p.code)} title="Copy" className="text-muted-foreground hover:text-foreground"><Copy className="h-3.5 w-3.5" /></button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{p.months} month(s) · used {p.uses}{p.max_uses ? `/${p.max_uses}` : " (∞)"}{p.note ? ` · ${p.note}` : ""}</p>
                </div>
                <button onClick={() => deletePromo(p.id)} data-testid={`promo-delete-${p.id}`} className="text-destructive shrink-0"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
